// Duplicate-student detection helpers.
//
// "Clever" matching: students entered twice rarely match byte-for-byte. The same
// person shows up as "+972-50-587-4407" / "0505874407" / "505874407", or as
// "محمد  أحمد" / "محمّد احمد". We normalise both phone and name so those collapse
// to the same key, then cluster everyone who shares a phone OR a name via a small
// union-find. A cluster of 2+ is a duplicate group the admin can act on.

// Strip Arabic tashkeel/tatweel and fold the letter variants that data-entry
// inconsistency produces (alef hamza forms, ة/ه, ى/ي) so "أحمد" === "احمد".
const ARABIC_DIACRITICS = /[ؐ-ًؚ-ٰٟۖ-ۭـ]/g;

export function normalizeName(...parts) {
  const raw = parts.filter(Boolean).join(' ');
  return String(raw)
    .replace(ARABIC_DIACRITICS, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ') // drop punctuation
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

// Reduce any phone shape to a canonical local form: digits only, country code
// (972/970, with or without a 00 prefix) stripped, a leading 0 guaranteed.
// Returns '' for anything too short to be a real, comparable number so blanks
// never cluster together.
export function normalizePhone(phone) {
  let digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.startsWith('972')) digits = digits.slice(3);
  else if (digits.startsWith('970')) digits = digits.slice(3);
  if (digits && !digits.startsWith('0')) digits = `0${digits}`;
  return digits.length >= 9 ? digits : '';
}

// Count the meaningful fields a record carries, used to pick which copy to keep
// (the most complete one) when defaulting the "keep" selection.
function completenessScore(s) {
  const fields = [s.phone, s.parentPhone, s.email, s.dateOfBirth, s.nationalId, s.guardianName, s.notes, s.middleName];
  return fields.reduce((n, f) => (String(f || '').trim() ? n + 1 : n), 0);
}

function createdMillis(s) {
  const v = s.createdAt;
  if (!v) return Number.POSITIVE_INFINITY; // unknown age sorts last (least likely "original")
  const d = v?.toDate ? v.toDate() : new Date(v);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
}

// The record we recommend keeping: most complete, then oldest (the likely
// original the later duplicate was a re-entry of).
//
// { prefer: 'intake' } (sport merge): the file with a signed intake form wins
// (it carries the signature + medical release), then the newest — an old
// paper-import file is the one folded into it.
export function pickPrimary(students, { prefer } = {}) {
  if (prefer === 'intake') {
    return [...students].sort((a, b) => {
      const ia = a.intakeId ? 1 : 0;
      const ib = b.intakeId ? 1 : 0;
      if (ia !== ib) return ib - ia;
      const ca = createdMillis(a) === Number.POSITIVE_INFINITY ? 0 : createdMillis(a);
      const cb = createdMillis(b) === Number.POSITIVE_INFINITY ? 0 : createdMillis(b);
      return cb - ca;
    })[0];
  }
  return [...students].sort((a, b) => {
    const c = completenessScore(b) - completenessScore(a);
    if (c !== 0) return c;
    return createdMillis(a) - createdMillis(b);
  })[0];
}

// Every phone a record can be reached on (own, parent, guardian).
export function phoneKeys(s) {
  return [...new Set([normalizePhone(s.phone), normalizePhone(s.parentPhone), normalizePhone(s.guardianMobile)].filter(Boolean))];
}

// Siblings share the parent's phone: two records on one number with different
// first names are more likely brother and sister than one child twice.
export function siblingHint(members) {
  const list = members || [];
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const a = list[i]; const b = list[j];
      const shared = phoneKeys(a).some((k) => phoneKeys(b).includes(k));
      const fa = normalizeName(a.firstName);
      const fb = normalizeName(b.firstName);
      if (shared && fa && fb && fa !== fb) return true;
    }
  }
  return false;
}

// Group students into duplicate clusters. Input items must carry a stable `id`
// plus name parts and phone(s). Returns clusters of 2+ students, each tagged
// with how its members matched ('phone' | 'name'), newest clusters first.
export function findDuplicateClusters(students, { prefer } = {}) {
  const list = (students || []).filter((s) => s && s.id != null);

  // Union-find over array indices.
  const parent = list.map((_, i) => i);
  const find = (i) => { while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; } return i; };
  const union = (a, b) => { const ra = find(a); const rb = find(b); if (ra !== rb) parent[ra] = rb; };

  const phoneBuckets = new Map(); // phoneKey → first index seen
  const nameBuckets = new Map();  // nameKey  → first index seen

  list.forEach((s, i) => {
    // A student may carry a personal and a parent phone; either matching another
    // record's number links them.
    for (const pk of phoneKeys(s)) {
      if (phoneBuckets.has(pk)) union(i, phoneBuckets.get(pk));
      else phoneBuckets.set(pk, i);
    }
    const nameKey = normalizeName(s.firstName, s.middleName, s.lastName);
    if (nameKey) {
      if (nameBuckets.has(nameKey)) union(i, nameBuckets.get(nameKey));
      else nameBuckets.set(nameKey, i);
    }
  });

  const groups = new Map(); // root → indices[]
  list.forEach((_, i) => {
    const r = find(i);
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r).push(i);
  });

  const clusters = [];
  for (const idxs of groups.values()) {
    if (idxs.length < 2) continue;
    const members = idxs.map((i) => list[i]);

    // Why did these match? Recompute against the cluster so the label is honest.
    const phoneSeen = new Map();
    const nameSeen = new Map();
    let byPhone = false;
    let byName = false;
    for (const m of members) {
      for (const pk of phoneKeys(m)) {
        if (phoneSeen.has(pk)) byPhone = true; else phoneSeen.set(pk, m.id);
      }
      const nk = normalizeName(m.firstName, m.middleName, m.lastName);
      if (nk) { if (nameSeen.has(nk)) byName = true; else nameSeen.set(nk, m.id); }
    }
    const reasons = [];
    if (byPhone) reasons.push('phone');
    if (byName) reasons.push('name');

    const primary = pickPrimary(members, { prefer });
    clusters.push({ id: primary.id, members, reasons, primaryId: primary.id, maybeSiblings: siblingHint(members) });
  }

  // Newest-first: clusters whose newest member was created most recently bubble
  // up, since those are the duplicates an admin most likely just created.
  clusters.sort((a, b) => {
    const an = Math.max(...a.members.map((m) => (createdMillis(m) === Infinity ? 0 : createdMillis(m))));
    const bn = Math.max(...b.members.map((m) => (createdMillis(m) === Infinity ? 0 : createdMillis(m))));
    return bn - an;
  });

  return clusters;
}
